// How undo and redo read and rewrite each kind of scene content through v1 (untracked), then bring the
// renderer (bridge messages) and the editor's state (Redux) along, as the original edit did. The same sync
// (sceneSync) brings them along for changes another client made, which the scene change stream reports.

import type { components } from '@/generated/contracts/api-v1';
import type { RendererBridge } from '@/modules/rendererBridge';
import { v1 } from '@/infrastructure/api/v1/client';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import { keyframeToLegacy } from '@/infrastructure/api/v1/mappers/keyframes';
import { clipInstanceToLegacy } from '@/infrastructure/api/v1/mappers/clipInstances';
import { uiElementInstanceToLegacy } from '@/infrastructure/api/v1/mappers/uiElementInstances';
import { uiElementTimelineRow } from '@/infrastructure/adapters/TimelineInitAdapter';
import { convertToAssetInstance, convertToClipInstance } from '@/utils/dataConverters';
import { convertToUIElementInstance } from '@/modules/uiElements';
import type { AssetInstance, SupabaseAssetInstance } from '@/types/assetTypes';
import type { SupabaseClipInstance } from '@/types/clipTypes';
import type { SupabaseKeyframe } from '@/modules/keyframes';
import type { SupabaseUIElementInstance } from '@/modules/uiElements';
import type { DeletionPort, KnownRowsPort, RereadPort, ScenePorts } from './sceneWrites';
import { ANY_MATERIAL, editorWrites, materialsOf, untracked } from './sceneWrites';
import { uiElementPort } from './uiElementPort';
import { contentPorts } from './contentPorts';
import { vfxPort } from './vfxPort';
import { sceneGate } from './sceneGate';
import { materialPort, materialsChanged, rowPorts } from './rowPorts';
import { announce } from './editorChanges';
import { list, read, readUnlessGone } from './v1Rows';
import { changedSince, confirmedAs, readStarted } from '@/modules/keyframes/localEdits';

type Schemas = components['schemas'];
type AssetInstanceDto = Schemas['AssetInstanceDto'];
type KeyframeDto = Schemas['KeyframeDto'];
type ClipInstanceDto = Schemas['ClipInstanceDto'];
type UiElementInstanceDto = Schemas['UiElementInstanceDto'];
type MaterialInstanceDto = Schemas['MaterialInstanceDto'];
type BundledVfxInstanceDto = Schemas['BundledVfxInstanceDto'];
type DeletionDto = Schemas['DeletionDto'];
type RestoredDeletionDto = Schemas['RestoredDeletionDto'];
type ChangedRowDto = Schemas['ChangedRowDto'];
type PlacedAssetInstanceDto = Schemas['PlacedAssetInstanceDto'];
type Bridge = Pick<RendererBridge, 'send'>;

const ANCHOR_CATEGORIES = new Set(['immersal', 'image_anchor', 'geospatial_anchor']);

/** Rows of a scene, each as its own v1 read returns it. */
export interface SceneRows {
  assetInstances?: AssetInstanceDto[];
  keyframes?: KeyframeDto[];
  clipInstances?: ClipInstanceDto[];
  uiElementInstances?: UiElementInstanceDto[];
  materialInstances?: MaterialInstanceDto[];
  vfxInstances?: BundledVfxInstanceDto[];
}

/** Ids of rows gone from a scene, by resource; materials as rows, since the renderer is told of a removed one by its row. */
export interface DeletedRows {
  assetInstances?: string[];
  keyframes?: string[];
  clipInstances?: string[];
  uiElementInstances?: string[];
  materialInstances?: MaterialInstanceDto[];
  vfxInstances?: string[];
}

/** A change's kind of row, named as SceneChangeEntity names it. */
export type SceneEntity = Schemas['SceneChangeEntity'];

// The store's modules reach back into services, so it is loaded when first needed
const editor = async () => {
  const [{ default: store }, sceneAssets, keyframes, clips, uiElements, triggers] = await Promise.all([
    import('@/store/store'),
    import('@/features/sceneAssets/sceneAssetsActions'),
    import('@/features/keyframes/keyframesSlice'),
    import('@/features/clips/clipsSlice'),
    import('@/features/uiElements/uiElementsSlice'),
    import('@/features/triggers/triggersSlice'),
  ]);
  const { toKeyframe } = await import('@/features/keyframes/keyframesThunks');
  const { triggerFromAssetInstance, isTriggerCategory } = await import('@/features/triggers/triggersThunks');
  return { store, sceneAssets, keyframes, clips, uiElements, triggers, toKeyframe, triggerFromAssetInstance, isTriggerCategory };
};

// The editor's timeline row for a saved asset instance: added, or replaced keeping its view state
async function showAsset(dto: AssetInstanceDto): Promise<void> {
  const e = await editor();
  const row = assetInstanceToLegacy(dto) as unknown as SupabaseAssetInstance;
  if (e.isTriggerCategory(dto.category)) {
    const prev = e.store.getState().triggers.entities[dto.uniqueId];
    e.store.dispatch(e.triggers.upsertTrigger(e.triggerFromAssetInstance(row, prev, dto.sourceName ?? undefined)));
    return;
  }
  listSceneAsset(e, dto.uniqueId, convertToAssetInstance(row));
}

function listSceneAsset(e: Awaited<ReturnType<typeof editor>>, id: string, asset: AssetInstance): void {
  const listed = (e.store.getState().sceneAssets.sceneAssetsArray as AssetInstance[]).find(a => a.instanceId === id);
  e.store.dispatch(listed
    ? e.sceneAssets.patchSceneAsset(id, { ...asset, isSelected: listed.isSelected, isMuted: listed.isMuted })
    : e.sceneAssets.addSceneAsset(asset));
}

async function hideAsset(id: string): Promise<void> {
  const e = await editor();
  const state = e.store.getState();
  if (state.triggers.entities[id]) e.store.dispatch(e.triggers.removeTrigger({ instanceId: id, assetId: id }));
  e.store.dispatch(e.sceneAssets.removeSceneAssets([id]));
  e.store.dispatch(e.keyframes.clearKeyframesForAsset(id));
  e.store.dispatch(e.clips.clearClipsForAsset(id));
}

// A saved row: what a failed save later goes back to. `at` is when the save that wrote it started
async function showKeyframe(dto: KeyframeDto, at?: number): Promise<void> {
  const e = await editor();
  const keyframe = e.toKeyframe(keyframeToLegacy(dto) as unknown as SupabaseKeyframe);
  confirmedAs(keyframe, at);
  e.store.dispatch(e.keyframes.upsertKeyframe(keyframe));
}

async function showClip(dto: ClipInstanceDto): Promise<void> {
  const e = await editor();
  e.store.dispatch(e.clips.upsertClip(convertToClipInstance(clipInstanceToLegacy(dto) as unknown as SupabaseClipInstance)));
}

async function showUiElement(dto: UiElementInstanceDto): Promise<void> {
  const e = await editor();
  const row = uiElementInstanceToLegacy(dto);
  e.store.dispatch(e.uiElements.addUIElement(convertToUIElementInstance(row as unknown as SupabaseUIElementInstance)));
  listSceneAsset(e, dto.uniqueId, convertToAssetInstance(uiElementTimelineRow(row) as unknown as SupabaseAssetInstance));
}

async function hideUiElement(dto: { id: string; sceneId: string }): Promise<void> {
  const e = await editor();
  e.store.dispatch(e.uiElements.removeUIElement({ instanceId: dto.id, sceneInstanceId: dto.sceneId }));
  e.store.dispatch(e.sceneAssets.removeSceneAssets([dto.id]));
}

async function hideKeyframe(id: string, assetInstanceId: string): Promise<void> {
  const e = await editor();
  e.store.dispatch(e.keyframes.removeKeyframe({ id, assetInstanceId }));
}

async function hideClip(dto: { id: string; assetInstanceId: string | null; sceneId: string }): Promise<void> {
  const e = await editor();
  e.store.dispatch(e.clips.removeClip({ instanceId: dto.id, assetId: dto.assetInstanceId ?? '', sceneInstanceId: dto.sceneId }));
}

/** The editor updates the sync makes, through `gate`: the undo stack's run only for the scene its press was made in. */
function editorUpdates(gate: ReturnType<typeof sceneGate>) {
  return {
    showAsset: gate.update(showAsset),
    hideAsset: gate.update(hideAsset),
    showKeyframe: gate.update(showKeyframe),
    hideKeyframe: gate.update(hideKeyframe),
    showClip: gate.update(showClip),
    hideClip: gate.update(hideClip),
    showUiElement: gate.update(showUiElement),
    hideUiElement: gate.update(hideUiElement),
    // Only what the scene the undo was pressed in shows; shared caches are told by the row ports themselves
    announce: gate.update(announce),
    // What the editor knows is of the open scene only
    remember: gate.update(rememberShown),
    forget: gate.update(forgetKnown),
  };
}
type EditorUpdates = ReturnType<typeof editorUpdates>;

/** getSceneVfx's instance as the renderer takes it: its library effect is the effect definition. */
export const bundledVfx = ({ vfxLibraryUniqueId, ...row }: Schemas['VfxInstanceDto']): BundledVfxInstanceDto => ({ ...row, vfxUniqueId: vfxLibraryUniqueId });

/** A row as the editor knows it, by its own read or this tab's save. */
type KnownRow = { uniqueId: string } & Record<string, unknown>;

/** A row a refresh showed: as the editor knew it once this tab's writes to it had answered, and as read (null when gone or hidden). */
export interface ShownRow {
  prior?: KnownRow;
  row: KnownRow | null;
}

/** What a refresh showed, or, when this tab wrote the row while it was read, what the read put off then shows. */
export type Refreshed = ShownRow | { later: Promise<Refreshed | undefined> };

/** How the renderer and the editor follow rows that came, changed or went underneath them. */
export interface SceneSync {
  /** Rows added or brought back: to the renderer as restored, taken exactly as sent, and into the editor. */
  showAdded(rows: SceneRows): Promise<void>;
  /** Rows changed. */
  showUpdated(rows: SceneRows): Promise<void>;
  /** Rows gone, with what went with them. */
  hideDeleted(rows: DeletedRows): Promise<void>;
  /** A restore's rows, and the rows outside it that it set back. */
  showRestored(restored: RestoredDeletionDto): Promise<void>;
  /** A delete's rows, and the rows outside it that it changed. */
  hideDeletion(deletion: DeletionDto): Promise<void>;
  /** Reads one row again and shows it as added or changed, or takes it out when it is gone; a row of another scene is left be. */
  refresh(entity: SceneEntity, id: string, sceneId: string, added: boolean): Promise<Refreshed | undefined>;
}

/** Runs a row's read put off while this tab wrote it, resolving with what it showed; one whose failure it keeps resolves with nothing. */
type ReadLater = (read: () => Promise<Refreshed | undefined>) => Promise<Refreshed | undefined>;

/** Runs a row's read put off while this tab wrote it; one that fails then is logged. */
const readNow: ReadLater = read => read().catch(error => {
  console.warn('[sceneSync] could not read a changed row again', error);
  return undefined;
});

/** Resolves once a refresh, and every read it put off, has shown the row. */
async function shownAtLast(refreshing: Promise<Refreshed | undefined>): Promise<void> {
  let refreshed = await refreshing;
  while (refreshed && 'later' in refreshed) refreshed = await refreshed.later;
}

/** The sync for renderer `bridge` with these editor updates (gated or not); `later` runs a read put off while this tab wrote its row. */
export function sceneSync(bridge: Bridge, update: EditorUpdates, later: ReadLater = readNow): SceneSync {
  const anchorsChanged = async (categories: Array<string | null | undefined>) => {
    if (categories.some(category => ANCHOR_CATEGORIES.has(category ?? ''))) await update.announce({ kind: 'anchors' });
  };

  const sync: SceneSync = {
    async showAdded(rows) {
      // Placements first, so a go_to_position clip finds its target and an effect or keyframe its placement
      await update.remember(rows);
      for (const assetInstance of rows.assetInstances ?? []) bridge.send({ type: 'assetInstanceAdded', assetInstance, restored: true });
      for (const vfxInstance of rows.vfxInstances ?? []) bridge.send({ type: 'vfxInstanceAdded', vfxInstance, restored: true });
      for (const keyframe of rows.keyframes ?? []) bridge.send({ type: 'keyframeAdded', keyframe, restored: true });
      for (const clipInstance of rows.clipInstances ?? []) bridge.send({ type: 'clipInstanceAdded', clipInstance, restored: true });
      for (const uiElementInstance of rows.uiElementInstances ?? []) bridge.send({ type: 'uiElementInstanceAdded', uiElementInstance, restored: true });
      for (const materialInstance of rows.materialInstances ?? []) {
        bridge.send({ type: 'materialInstanceUpserted', materialInstance });
      }
      for (const dto of rows.assetInstances ?? []) await update.showAsset(dto);
      for (const dto of rows.keyframes ?? []) await update.showKeyframe(dto);
      for (const dto of rows.clipInstances ?? []) await update.showClip(dto);
      for (const dto of rows.uiElementInstances ?? []) await update.showUiElement(dto);
      for (const id of new Set((rows.materialInstances ?? []).map(row => row.assetInstanceUniqueId))) materialsChanged(id);
      await anchorsChanged((rows.assetInstances ?? []).map(dto => dto.category));
    },

    async showUpdated(rows) {
      // Lock and visibility reach the renderer only by their own messages, sent when they differ from the row as last known
      const known = new Map((rows.assetInstances ?? []).map(dto => [dto.uniqueId, knownRows.get(`asset:${dto.uniqueId}`)]));
      await update.remember(rows);
      for (const dto of rows.assetInstances ?? []) {
        const was = known.get(dto.uniqueId);
        bridge.send({ type: 'assetInstanceUpdated', assetInstance: dto });
        if (!was || !!was.isLocked !== !!dto.isLocked) bridge.send({ type: 'assetInstanceLockChanged', assetInstance: dto });
        if (!was || (was.isActive ?? true) !== (dto.isActive ?? true)) bridge.send({ type: 'assetInstanceVisibilityChanged', assetInstance: dto });
        await update.showAsset(dto);
      }
      for (const keyframe of rows.keyframes ?? []) {
        bridge.send({ type: 'keyframeUpdated', keyframe });
        await update.showKeyframe(keyframe);
      }
      for (const clipInstance of rows.clipInstances ?? []) {
        bridge.send({ type: 'clipInstanceUpdated', clipInstance });
        await update.showClip(clipInstance);
      }
      for (const uiElementInstance of rows.uiElementInstances ?? []) {
        bridge.send({ type: 'uiElementInstanceUpdated', uiElementInstance });
        await update.showUiElement(uiElementInstance);
      }
      for (const vfxInstance of rows.vfxInstances ?? []) bridge.send({ type: 'vfxInstanceUpdated', vfxInstance });
      for (const materialInstance of rows.materialInstances ?? []) {
        bridge.send({ type: 'materialInstanceUpserted', materialInstance });
      }
      for (const id of new Set((rows.materialInstances ?? []).map(row => row.assetInstanceUniqueId))) materialsChanged(id);
      await anchorsChanged((rows.assetInstances ?? []).map(dto => dto.category));
    },

    async hideDeleted(rows) {
      await update.forget(rows);
      const { store } = await editor();
      const state = store.getState();
      const goneAssets = new Set(rows.assetInstances ?? []);
      const shown = state.sceneAssets.sceneAssetsArray as AssetInstance[];
      const categories = shown.filter(a => goneAssets.has(a.instanceId)).map(a => a.instanceCategory);
      // A placement's removal stands for its keyframes, clips, effects and materials in the renderer
      for (const vfxInstanceId of rows.vfxInstances ?? []) bridge.send({ type: 'vfxInstanceRemoved', vfxInstanceUniqueId: vfxInstanceId });
      for (const materialInstance of rows.materialInstances ?? []) {
        if (goneAssets.has(materialInstance.assetInstanceUniqueId)) continue;
        bridge.send({ type: 'materialInstanceRemoved', materialInstance });
        materialsChanged(materialInstance.assetInstanceUniqueId);
      }
      for (const id of rows.keyframes ?? []) {
        const keyframe = state.keyframes.entities[id];
        if (!keyframe || goneAssets.has(keyframe.assetInstanceId)) continue;
        bridge.send({ type: 'keyframeRemoved', keyframeUniqueId: id, assetInstanceUniqueId: keyframe.assetInstanceId });
        await update.hideKeyframe(id, keyframe.assetInstanceId);
      }
      for (const id of rows.clipInstances ?? []) {
        const clip = state.clips.entities[id];
        if (clip && goneAssets.has(clip.assetId)) continue;
        bridge.send({ type: 'clipInstanceRemoved', clipInstanceUniqueId: id });
        if (clip) await update.hideClip({ id, assetInstanceId: clip.assetId, sceneId: clip.sceneInstanceId });
      }
      for (const id of rows.uiElementInstances ?? []) {
        bridge.send({ type: 'uiElementInstanceRemoved', uiElementInstanceUniqueId: id });
        const ui = state.uiElements.entities[id];
        await update.hideUiElement({ id, sceneId: ui?.sceneInstanceId ?? '' });
      }
      for (const id of goneAssets) {
        bridge.send({ type: 'assetInstanceRemoved', assetInstanceUniqueId: id });
        await update.hideAsset(id);
      }
      await anchorsChanged(categories);
    },

    async showRestored(restored) {
      await sync.showAdded(restored);
      if (restored.scenes.length > 0 || restored.stories.length > 0) {
        for (const scene of restored.scenes) await update.announce({ kind: 'scene', sceneId: scene.uniqueId });
        for (const story of restored.stories) announce({ kind: 'story', storyId: story.uniqueId });
      }
      if (restored.assetTypes.length > 0) announce({ kind: 'library' });
      for (const preset of restored.materialTypes) announce({ kind: 'materialPreset', materialTypeId: preset.uniqueId });
      await refreshChanged(restored.changed);
    },

    async hideDeletion(deletion) {
      const { deleted } = deletion;
      const materials = deleted.materialInstances.length > 0 ? await materialRows(deleted.materialInstances) : [];
      await sync.hideDeleted({ ...deleted, materialInstances: materials });
      if (deleted.assetTypes.length > 0) announce({ kind: 'library' });
      for (const materialTypeId of deleted.materialTypes) announce({ kind: 'materialPreset', materialTypeId });
      await refreshChanged(deletion.changed);
    },

    async refresh(entity, id, sceneId, added) {
      const row = WRITTEN_AS[entity]?.(id);
      if (!row) {
        await announceRow(entity, id, sceneId, added);
        return undefined;
      }
      // Read once this tab's writes to it have answered, and again later when one to it began or a keyframe edit ran meanwhile
      const mark = await editorWrites.quiet([row]);
      const prior = knownRows.get(entity === 'materialInstance' ? `material:${id}` : row);
      const readAt = readStarted();
      const read = await readRow(entity, id, sceneId, added);
      if (editorWrites.touchedSince(mark, [row]) || (entity === 'keyframe' && changedSince(id, readAt))) {
        const shownLater = new Promise<void>(resolve => setTimeout(resolve, READ_AGAIN_MS))
          .then(() => (currentScene() === sceneId ? later(() => sync.refresh(entity, id, sceneId, added)) : undefined));
        return { later: shownLater };
      }
      if (!read || currentScene() !== sceneId) return undefined;
      await read.show();
      return { prior, row: read.row };
    },
  };

  // One row of the scene's content read again, as it is shown, and how to show it; nothing when it is another scene's
  async function readRow(entity: SceneEntity, id: string, sceneId: string, added: boolean): Promise<{ row: KnownRow | null; show: () => Promise<void> } | null> {
    const show = added ? sync.showAdded : sync.showUpdated;
    switch (entity) {
      case 'assetInstance': {
        const dto = await readUnlessGone(v1.GET('/api/v1/asset-instances/{uniqueId}', { params: { path: { uniqueId: id } } }));
        if (dto && dto.sceneUniqueId !== sceneId) return null;
        return { row: dto, show: () => (dto ? show({ assetInstances: [dto] }) : sync.hideDeleted({ assetInstances: [id] })) };
      }
      case 'keyframe': {
        const dto = await readUnlessGone(v1.GET('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: id } } }));
        if (dto && dto.sceneUniqueId !== sceneId) return null;
        return { row: dto, show: () => (dto ? show({ keyframes: [dto] }) : sync.hideDeleted({ keyframes: [id] })) };
      }
      case 'clipInstance': {
        const dto = await readUnlessGone(v1.GET('/api/v1/clip-instances/{uniqueId}', { params: { path: { uniqueId: id } } }));
        if (dto && dto.sceneUniqueId !== sceneId) return null;
        return { row: dto, show: () => (dto ? show({ clipInstances: [dto] }) : sync.hideDeleted({ clipInstances: [id] })) };
      }
      case 'uiElementInstance': {
        const dto = await readUnlessGone(v1.GET('/api/v1/ui-element-instances/{uniqueId}', { params: { path: { uniqueId: id } } }));
        if (dto && dto.sceneUniqueId !== sceneId) return null;
        // An inactive screen message is not shown, as the editor loads none
        const shown = dto && dto.isActive !== false ? dto : null;
        return { row: shown, show: () => (shown ? show({ uiElementInstances: [shown] }) : sync.hideDeleted({ uiElementInstances: [id] })) };
      }
      case 'materialInstance': {
        const rows = await list(page => v1.GET('/api/v1/material-instances', { params: { query: { sceneUniqueId: [sceneId], ...page } } }));
        const dto = rows.find(row => row.uniqueId === id);
        if (dto) return { row: dto, show: () => show({ materialInstances: [dto] }) };
        // Gone: the renderer is told by its row as last known, and its asset's materials are read again
        const known = knownRows.get(`material:${id}`) as MaterialInstanceDto | undefined;
        return known ? { row: null, show: () => sync.hideDeleted({ materialInstances: [known] }) } : null;
      }
      case 'vfxInstance': {
        const vfx = await read(v1.GET('/api/v1/scenes/{sceneUniqueId}/vfx', { params: { path: { sceneUniqueId: sceneId } } }));
        const found = vfx.instances.find(row => row.uniqueId === id);
        const dto = found ? bundledVfx(found) : null;
        return { row: dto, show: () => (dto ? show({ vfxInstances: [dto] }) : sync.hideDeleted({ vfxInstances: [id] })) };
      }
      default:
        return null;
    }
  }

  // Rows that are not the scene's content: the parts of the editor that show them read them again
  async function announceRow(entity: SceneEntity, id: string, sceneId: string, added: boolean): Promise<void> {
    switch (entity) {
      case 'scene':
        return update.announce({ kind: 'scene', sceneId: id });
      case 'story':
        return announce({ kind: 'story', storyId: id });
      case 'assetType': {
        const dto = await readUnlessGone(v1.GET('/api/v1/asset-types/{assetTypeUniqueId}', { params: { path: { assetTypeUniqueId: id } } }));
        if (dto && !added) announce({ kind: 'libraryItem', assetTypeId: id, displayName: dto.displayName });
        else announce({ kind: 'library' });
        return;
      }
      case 'image':
        // An image of the scene or its story: the image library and the scene's cover read again
        announce({ kind: 'library' });
        return update.announce({ kind: 'scene', sceneId });
      // Library rows the scene uses, as undo announces its own changes to them
      case 'clipType':
        return announce({ kind: 'clipType', clipTypeId: id });
      case 'materialType':
        return announce({ kind: 'materialPreset', materialTypeId: id });
      case 'uiElementType':
        return announce({ kind: 'screenMessageStyle', typeId: id });
    }
  }

  // The rows of deleted materials, as last seen; a placement's own go with it in the renderer anyway
  async function materialRows(ids: string[]): Promise<MaterialInstanceDto[]> {
    return ids.map(id => knownRows.get(`material:${id}`) as MaterialInstanceDto | undefined).filter((row): row is MaterialInstanceDto => !!row);
  }

  // Rows outside a deletion that it changed, or a restore set back: read again
  async function refreshChanged(changed: ChangedRowDto[]): Promise<void> {
    for (const row of changed) {
      const entity = CHANGED_ENTITIES[row.resource];
      if (!entity) continue;
      await sync.refresh(entity, row.uniqueId, currentScene(), false).catch(error => console.warn('[sceneSync] could not read a changed row again', error));
    }
  }

  return sync;
}

/** How long a row this tab wrote while it was read waits to be read again. */
const READ_AGAIN_MS = 1500;

/** How this tab's writes in flight name a row of each kind of the scene's content. */
const WRITTEN_AS: Partial<Record<SceneEntity, (id: string) => string>> = {
  assetInstance: id => `asset:${id}`,
  keyframe: id => `keyframe:${id}`,
  clipInstance: id => `clip:${id}`,
  uiElementInstance: id => `uiElement:${id}`,
  // Every material write is also one of any material's, whichever asset it is on
  materialInstance: () => ANY_MATERIAL,
  vfxInstance: id => `vfx:${id}`,
};

/** The resources a deletion names, as the change stream names their rows. */
const CHANGED_ENTITIES: Record<string, SceneEntity> = {
  assetInstances: 'assetInstance',
  keyframes: 'keyframe',
  clipInstances: 'clipInstance',
  uiElementInstances: 'uiElementInstance',
  materialInstances: 'materialInstance',
  vfxInstances: 'vfxInstance',
  scenes: 'scene',
  stories: 'story',
  assetTypes: 'assetType',
};

/**
 * The open scene's rows as last read, by `kind:id` (the undo stack's names): a deleted material can still be named
 * to the renderer by its row, and a change from elsewhere can be undone back to what the editor held.
 */
export const knownRows = new Map<string, KnownRow>();

const ROW_KINDS = {
  assetInstances: 'asset',
  keyframes: 'keyframe',
  clipInstances: 'clip',
  uiElementInstances: 'uiElement',
  materialInstances: 'material',
  vfxInstances: 'vfx',
} as const;

/** Rows this tab saved in a batch, which answers no rows: knownRows may hold them as they were before. */
const savedUnread = new Set<string>();

/** Whether this tab saved the row (`kind:id`) since it was last read, so a difference from knownRows may be its own edit. */
export const savedSinceRead = (key: string): boolean => savedUnread.has(key);

/** Notes rows (`kind:id`) this tab saved without reading back what the server made of them, as a batch does. */
export function savedUnreadRows(keys: string[]): void {
  for (const key of keys) if (knownRows.has(key)) savedUnread.add(key);
}

/** Rows a failed write of this tab named (`kind:id`, a placement's materials by `materials:id`), which the editor and the renderer may show unsaved until they are shown as read. */
const unconfirmed = new Set<string>();

/** Whether a failed write left this row of the kind unsure since it was last shown as read, however it compares with knownRows. */
export const isUnconfirmed = (kind: string, row: { uniqueId: string; assetInstanceUniqueId?: unknown }): boolean =>
  unconfirmed.has(`${kind}:${row.uniqueId}`)
  || (kind === 'material' && typeof row.assetInstanceUniqueId === 'string' && unconfirmed.has(materialsOf(row.assetInstanceUniqueId)));

/** Notes rows as the editor now holds them. */
export function remember(rows: SceneRows): void {
  for (const [resource, kind] of Object.entries(ROW_KINDS)) {
    for (const row of (rows[resource as keyof SceneRows] ?? []) as Array<{ uniqueId: string }>) {
      knownRows.set(`${kind}:${row.uniqueId}`, row as { uniqueId: string } & Record<string, unknown>);
      savedUnread.delete(`${kind}:${row.uniqueId}`);
    }
  }
}

// Rows shown to the editor and the renderer as read: remembered, and sure again
function rememberShown(rows: SceneRows): void {
  remember(rows);
  for (const [resource, kind] of Object.entries(ROW_KINDS)) {
    for (const row of (rows[resource as keyof SceneRows] ?? []) as Array<{ uniqueId: string; assetInstanceUniqueId?: string }>) {
      unconfirmed.delete(`${kind}:${row.uniqueId}`);
      if (kind === 'material' && row.assetInstanceUniqueId) unconfirmed.delete(materialsOf(row.assetInstanceUniqueId));
    }
  }
}

const forget = (key: string) => {
  knownRows.delete(key);
  savedUnread.delete(key);
  unconfirmed.delete(key);
};

function forgetKnown(rows: DeletedRows): void {
  for (const [resource, kind] of Object.entries(ROW_KINDS)) {
    for (const row of (rows[resource as keyof DeletedRows] ?? []) as Array<string | { uniqueId: string }>) {
      forget(`${kind}:${typeof row === 'string' ? row : row.uniqueId}`);
    }
  }
}

/** The kinds of the scene's content whose writes the editor knows the rows of, by the resource they are listed under. */
const SAVED_AS: Partial<Record<string, keyof SceneRows>> = {
  asset: 'assetInstances',
  keyframe: 'keyframes',
  clip: 'clipInstances',
  uiElement: 'uiElementInstances',
  material: 'materialInstances',
};

/** knownRows kept as this tab's own writes leave the open scene's rows, so a change from elsewhere is told from them. */
export const knownRowsPort: KnownRowsPort = {
  saved(write, body, answer) {
    // A delete (a library item's with its placements too) answers every row it took
    if (write.op === 'delete') {
      const deleted = (answer as DeletionDto | null)?.deleted;
      for (const [listed, kind] of Object.entries(ROW_KINDS)) {
        for (const id of deleted?.[listed as keyof typeof ROW_KINDS] ?? []) forget(`${kind}:${id}`);
      }
      return;
    }
    const resource = SAVED_AS[write.kind];
    if (!resource) return;
    // A batch of changes answers none of its rows: they are known to have changed since they were read
    if (write.op === 'batch' && write.kind !== 'material') {
      for (const item of (body as { items?: Array<{ uniqueId?: unknown }> } | undefined)?.items ?? []) {
        if (knownRows.has(`${write.kind}:${item.uniqueId}`)) savedUnread.add(`${write.kind}:${item.uniqueId}`);
      }
      return;
    }
    if (!answer) return;
    // A placement answers with the keyframes and clips it was made with
    const placed = write.kind === 'asset' && write.op === 'create' ? (answer as PlacedAssetInstanceDto) : null;
    const rows: SceneRows = placed
      ? { assetInstances: [placed.assetInstance], keyframes: placed.keyframes, clipInstances: placed.clipInstances }
      : { [resource]: Array.isArray(answer) ? answer : [answer] };
    // An undo of a scene since closed writes that scene's rows, which are not this scene's to know
    const inScene = (row: { sceneUniqueId?: string | null }) => row.sceneUniqueId === openSceneId;
    remember({
      assetInstances: rows.assetInstances?.filter(inScene),
      keyframes: rows.keyframes?.filter(inScene),
      clipInstances: rows.clipInstances?.filter(inScene),
      uiElementInstances: rows.uiElementInstances?.filter(inScene),
      // A material names its placement, not its scene
      materialInstances: rows.materialInstances?.filter(row => knownRows.has(`asset:${row.assetInstanceUniqueId}`)),
    });
  },
};

let openSceneId = '';

/** The scene the editor has open, for reads that list by scene. */
export const currentScene = () => openSceneId;

/** Notes the scene the editor has open. */
export function setCurrentScene(sceneId: string | null): void {
  openSceneId = sceneId ?? '';
  knownRows.clear();
  savedUnread.clear();
  unconfirmed.clear();
}

/** The sync for changes from elsewhere (the scene change stream), applied to the renderer and the editor while `live`. */
export function liveSceneSync(bridge: Bridge, live: () => boolean, later?: ReadLater): SceneSync {
  const gate = sceneGate(live);
  return sceneSync(gate.bridge(bridge), editorUpdates(gate), later);
}

/** How the change stream names a row of each kind the undo stack names. */
const ENTITY_OF: Record<string, SceneEntity> = {
  asset: 'assetInstance',
  keyframe: 'keyframe',
  clip: 'clipInstance',
  uiElement: 'uiElementInstance',
  scene: 'scene',
  story: 'story',
  assetType: 'assetType',
  uiElementType: 'uiElementType',
  materialType: 'materialType',
  vfx: 'vfxInstance',
};

/** Reads failed writes' rows again for `renderer`, each shown only while the scene it was written in is still open. */
export function rereadPort(renderer: Bridge): RereadPort {
  return {
    async failedWrite({ write, body, rows, sceneId }) {
      const gate = sceneGate(() => currentScene() === sceneId);
      // A read put off while this tab wrote the row is waited for, and one that fails fails this, so it is read again later
      const sync = sceneSync(gate.bridge(renderer), editorUpdates(gate), read => read());
      // Until shown as read, a read of the scene shows them again even where they match what it knows
      if (currentScene() === sceneId) rows.forEach(row => unconfirmed.add(row));
      if (write.kind === 'material') {
        if (write.op === 'patch' || write.op === 'delete') {
          await shownAtLast(sync.refresh('materialInstance', write.id, sceneId, false));
          return;
        }
        // An upsert names its nodes by placement: those placements' materials are read again
        const items = (write.op === 'batch' ? (body as { items?: unknown[] } | undefined)?.items : [body]) ?? [];
        const ids = [...new Set(items.map(item => (item as { assetInstanceUniqueId?: unknown } | undefined)?.assetInstanceUniqueId))]
          .filter((id): id is string => typeof id === 'string');
        if (ids.length === 0) return;
        // Read once this tab's writes to their materials have answered, and again when one began during the read
        const keys = ids.map(materialsOf);
        const readSaved = () => list(page => v1.GET('/api/v1/material-instances', { params: { query: { assetInstanceUniqueId: ids, ...page } } }));
        let mark = await editorWrites.quiet(keys);
        let saved = await readSaved();
        while (editorWrites.touchedSince(mark, keys)) {
          mark = await editorWrites.quiet(keys);
          saved = await readSaved();
        }
        // Those the editor knows on them that the read no longer has are gone: taken out by their rows as last known, before what replaced them is shown
        const live = new Set(saved.map(row => row.uniqueId));
        const gone = [...knownRows.entries()]
          .filter(([key, row]) => key.startsWith('material:') && ids.includes(String(row.assetInstanceUniqueId)) && !live.has(row.uniqueId))
          .map(([, row]) => row as unknown as MaterialInstanceDto);
        if (gone.length > 0) await sync.hideDeleted({ materialInstances: gone });
        await sync.showUpdated({ materialInstances: saved });
        const told = new Set([...saved, ...gone].map(row => row.assetInstanceUniqueId));
        for (const id of ids) if (!told.has(id)) materialsChanged(id);
        return;
      }
      // A create's rows are shown as added: the renderer hears of a placement only once it has saved
      for (const row of rows) {
        const at = row.indexOf(':');
        const entity = ENTITY_OF[row.slice(0, at)];
        if (entity) await shownAtLast(sync.refresh(entity, row.slice(at + 1), sceneId, write.op === 'create'));
      }
    },
  };
}

/** Restores deletions (restoreDeletion) through `sync`. */
export function deletionPort(sync: SceneSync): DeletionPort {
  return {
    async restore(deletionId) {
      const restored = await read(v1.POST('/api/v1/deletions/{deletionUniqueId}/restore', { params: { path: { deletionUniqueId: deletionId } }, headers: untracked.headers }));
      await sync.showRestored(restored);
    },
  };
}

/**
 * The undo stack's ports for the open editor. `live` says whether the undo or redo using them started in the
 * scene still open; when it did not, their writes still go through but the renderer and the editor are left be.
 */
export function scenePorts(renderer: RendererBridge, live: () => boolean): ScenePorts {
  const gate = sceneGate(live);
  const bridge = gate.bridge(renderer);
  const update = editorUpdates(gate);
  const sync = sceneSync(bridge, update);

  // A material's rows are a cache per asset instance, refreshed whichever scene is open; a write of one is ordered by its asset
  const material = {
    ...materialPort(bridge, sync),
    assetOf: (materialId: string) => {
      const assetId = knownRows.get(`material:${materialId}`)?.assetInstanceUniqueId;
      return typeof assetId === 'string' ? assetId : undefined;
    },
  };
  const { asset, keyframe, clip } = contentPorts(bridge, update, sync);
  const uiElement = uiElementPort(bridge, update, sync);

  // Scene settings are announced to the scene they belong to; the shared library and presets always
  const { scene, story, assetType, uiElementType, materialType } = rowPorts(update.announce, sync);

  return {
    asset, keyframe, clip, uiElement, material, scene, story, assetType, uiElementType, materialType, vfx: vfxPort(sync),
    deletions: deletionPort(sync), reread: rereadPort(renderer), known: knownRowsPort,
  };
}
